import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import CloseIcon, { SIZES } from 'components/CloseIcon';
import IconButton from 'components/IconButton';
import Image from 'components/ImageV2';
import LoadingState from 'components/LoadingState';
import NumberStepper from 'components/NumberStepper';
import ShoprunnerLineItemIcon from 'components/Shoprunner/ShoprunnerLineItemIcon';
import { IMAGE_RATIOS, IMAGE_WIDTHS, IMAGE_BG_COLOR } from 'constants/images';
import { mediumUp } from 'constants/mediaQueries';
import CartLineItemModel from 'models/CartLineItem';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const IMAGE_SIZE = 100;
const SPACING_LARGE = Theme.get('spacing') * 4;
const SPACING_SMALL = Theme.get('spacing') * 2;

const CartLineItem = ({
  item,
  remove,
  increment,
  decrement,
  isFinalSale,
  isBeingRemoved,
  isBeingUpdated,
  showShoprunner,
  enableDiscountPriceColor,
}) => {
  const isBeingModified = isBeingUpdated || isBeingRemoved;
  const isDiscounted = !!item.totalDiscount || !!item.compareAtPrice;
  const showQuantitySelector = item?.available && !item?.isDigitalGiftCardWithRecipientEmail;
  const showItemSoldOut = !item?.available;

  const handleIncrement = () => {
    if (!isBeingModified) {
      increment();
      const container = document.getElementById('drawer-body');
      container?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleDecrement = () => {
    if (!isBeingModified) {
      decrement();
    }
  };

  const handleRemove = () => {
    if (!isBeingModified) {
      remove();
    }
  };

  return (
    <div
      className={classNames('CartLineItem', {
        'CartLineItem--red-sale-price': enableDiscountPriceColor && isDiscounted,
      })}
      key={`CartLineItem--${item.id}`}
    >
      {isBeingModified && <LoadingState />}
      <div className="CartLineItem__product">
        <div className="CartLineItem__image-container">
          <a href={item.url} className="CartLineItem__link CartLineItem__link--image">
            <Image
              src={item.image}
              alt={item.imageAltText}
              className="CartLineItem__image"
              transformations={{ backgroundColor: IMAGE_BG_COLOR }}
              width={IMAGE_WIDTHS.W_200}
              ratio={IMAGE_RATIOS.R_1x1}
            />
          </a>
        </div>
        <div className="CartLineItem__details-container">
          <a href={item.url} className="CartLineItem__link">
            <div className="CartLineItem-details">
              <p className="CartLineItem__master-product-name">{item.masterProductName}</p>
              {item.color && <p className="CartLineItem__detail">{item.color}</p>}
              {!item.isUnisize && !item.product.isGiftCard && item.size && (
                <p className="CartLineItem__detail">
                  {I18n.localize('cart.lineItem.size')}: {item.size}
                </p>
              )}
              {item?.isDigitalGiftCardWithRecipientEmail && (
                <>
                  <p className="CartLineItem__detail">
                    {I18n.localize('cart.lineItem.dgc.recipientEmail')}
                    {item?.digitalGiftCardRecipientEmail}
                  </p>
                  <p className="CartLineItem__detail">
                    {I18n.localize('cart.lineItem.dgc.recipientName')}
                    {item?.digitalGiftCardRecipientName}
                  </p>
                  <p className="CartLineItem__detail">
                    {I18n.localize('cart.lineItem.dgc.recipientSendOn')}{' '}
                    {item?.digitalGiftCardRecipientSendOn}
                  </p>
                </>
              )}
              {item.isBackordered && (
                <p className="CartLineItem__detail CartLineItem__detail--danger">
                  {item.backorderMessage}
                </p>
              )}
              {isFinalSale && (
                <p className="CartLineItem__detail-finalsale CartLineItem__detail--danger">
                  {I18n.localize('cart.finalSaleMessage')}
                </p>
              )}
              {showShoprunner && <ShoprunnerLineItemIcon />}
            </div>
          </a>
          {showQuantitySelector && (
            <div className="CartLineItem__quantity-selector">
              <NumberStepper
                value={item.quantity}
                onStepUp={handleIncrement}
                onStepDown={handleDecrement}
                ariaLabel={I18n.localize('cart.lineItem.quantity')}
              />
            </div>
          )}
          {showItemSoldOut && (
            <p className="CartLineItem__soldout-message">{I18n.localize('pdp.item-sold-out')}</p>
          )}
        </div>
      </div>

      <div className="CartLineItem__price">
        <div className="CartLineItem__price-flex">
          <div className="CartLineItem__close-button">
            <IconButton onClick={handleRemove} aria-label={I18n.localize('cart.lineItem.remove')}>
              <CloseIcon size={SIZES.SMALL} />
            </IconButton>
          </div>
          <ul className="CartLineItem__price-list">
            <li className="CartLineItem__formatted-price">{item?.formattedPrice}</li>
            {isDiscounted && (
              <li className="CartLineItem__original-price">
                {item?.compareAtPrice
                  ? item?.formattedCompareAtPrice
                  : item?.formattedOriginalPrice}
              </li>
            )}
          </ul>
        </div>
      </div>
      <style jsx>
        {`
          .CartLineItem {
            position: relative;
            display: flex;
            justify-content: space-between;
            background-color: ${Theme.get('colors-white')};
          }

          .CartLineItem__product {
            display: flex;
            overflow: hidden;
            padding: ${SPACING_SMALL}px ${SPACING_SMALL}px ${SPACING_SMALL}px ${SPACING_LARGE}px;
          }

          @media ${mediumUp} {
            .CartLineItem__product {
              padding: ${SPACING_LARGE}px ${SPACING_SMALL}px ${SPACING_LARGE}px ${SPACING_LARGE}px;
            }
          }

          .CartLineItem__image-container {
            width: ${IMAGE_SIZE}px;
            min-width: ${IMAGE_SIZE}px;
            margin-right: ${SPACING_LARGE}px;
          }

          .CartLineItem__link {
            display: block;
            text-decoration: none;
            outline: none;
          }

          .CartLineItem__link.focused {
            outline: 2px solid ${Theme.get('colors-black')};
          }

          .CartLineItem__link--image {
            background-color: ${IMAGE_BG_COLOR};
          }

          .CartLineItem__master-product-name {
            margin: 0;
            line-height: 20px;
            color: ${Theme.get('colors-black')};
            font-size: ${Theme.get('font-size-px')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-weight: ${Theme.get('font-weight-semi-bold')};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .CartLineItem__details-container {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            overflow: hidden;
          }

          .CartLineItem__detail {
            margin: 0;
            line-height: 20px;
            color: ${Theme.get('colors-black')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-size: ${Theme.get('font-size-small-px')};
            font-weight: ${Theme.get('font-weight-normal')};
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .CartLineItem__detail-finalsale {
            margin: ${Theme.get('spacing-px')} 0 0 0;
            line-height: 16px;
            color: ${Theme.get('colors-black')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-size: ${Theme.get('font-size-small-px')};
            font-weight: ${Theme.get('font-weight-normal')};
          }

          .CartLineItem__detail--danger {
            color: ${Theme.get('colors-error')};
          }

          .CartLineItem__price-flex {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            align-items: flex-end;
            height: 100%;
          }

          .CartLineItem__price-list {
            display: flex;
            margin: 0 ${SPACING_LARGE}px ${Theme.get('spacing') * 2}px 0;
          }

          .CartLineItem__original-price {
            margin-left: ${Theme.get('spacing') * 1.25}px;
            letter-spacing: ${Theme.get('letter-spacing-px')};
            color: ${Theme.get('colors-discount-grey')};
            font-weight: ${Theme.get('font-weight-normal')};
            font-size: ${Theme.get('font-size-description-px')};
            text-decoration: line-through;
            line-height: 28px;
            text-align: right;
          }

          .CartLineItem__formatted-price {
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-weight: ${Theme.get('font-weight-normal')};
            font-size: ${Theme.get('font-size-description-px')};
            line-height: 28px;
            white-space: nowrap;
          }

          .CartLineItem--red-sale-price .CartLineItem__formatted-price {
            color: ${Theme.get('colors-discount-red')};
          }

          .CartLineItem__soldout-message {
            color: ${Theme.get('colors-discount-red')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-size: ${Theme.get('font-size-small-px')};
            font-weight: ${Theme.get('font-weight-normal')};
            white-space: nowrap;
          }

          .CartLineItem__quantity-selector {
            width: 90px;
          }

          .CartLineItem__close-button {
            margin: ${SPACING_SMALL}px ${SPACING_SMALL}px 0 0;
          }

          @media ${mediumUp} {
            .CartLineItem__original-price,
            .CartLineItem__formatted-price {
              line-height: 30px;
            }

            .CartLineItem-details {
              margin-bottom: ${SPACING_SMALL}px;
            }

            .CartLineItem__price-list {
              margin: 0 ${SPACING_LARGE}px ${Theme.get('spacing') * 4}px 0;
            }
          }
        `}
      </style>
      <style jsx global>
        {`
          .CartLineItem__close-button .IconButton:hover .CloseIcon--hoverable:before,
          .CartLineItem__close-button .IconButton:focus .CloseIcon--hoverable:before {
            transform: translate(-50%, -50%) rotate(135deg);
          }

          .CartLineItem__close-button .IconButton:hover .CloseIcon--hoverable:after,
          .CartLineItem__close-button .IconButton:focus .CloseIcon--hoverable:after {
            transform: translate(-50%, -50%) rotate(45deg);
          }
        `}
      </style>
    </div>
  );
};

CartLineItem.propTypes = {
  item: PropTypes.instanceOf(CartLineItemModel),
  remove: PropTypes.func.isRequired,
  increment: PropTypes.func.isRequired,
  decrement: PropTypes.func.isRequired,
  isBeingRemoved: PropTypes.bool,
  isBeingUpdated: PropTypes.bool,
  isFinalSale: PropTypes.bool,
  showShoprunner: PropTypes.bool,
  enableDiscountPriceColor: PropTypes.bool,
};

CartLineItem.defaultProps = {
  item: null,
  isBeingRemoved: false,
  isBeingUpdated: false,
  showShoprunner: false,
  isFinalSale: false,
  enableDiscountPriceColor: false,
};

export default CartLineItem;
